// src/pages/Reports/IssuanceReporting/AgentPrintHouseSummary/AgentSummaryTab.tsx
// RPT-012 (README.md mục 7) — tổng hợp theo đại lý, gộp MỌI kỳ phát hành (khác RPT-009 là đối
// soát theo TỪNG kỳ). "Khu vực" không có trong hệ thống — đã xác nhận giới hạn này với người dùng.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Col, Row, Spinner } from 'reactstrap';
import { toast } from 'react-toastify';

import DataTableShell, { getSttNumber } from 'Components/Common/DataTableShell';
import AgentPagination from 'pages/AgentManagement/AgentPagination';
import { getAgentSummaryReportApi } from 'helpers/fakebackend_helper';
import { ISSUANCE_REPORTS_BASE } from 'helpers/url_helper';
import { buildAgentMap, getAgentCode, getAgentName, loadAgentsWithCache } from 'helpers/agentLookup';
import { IAgent } from 'slices/agentManagement/types';
import AgentSelect from '../shared/AgentSelect';
import { cleanParams, formatPercent, formatQty, formatCurrency, getPaginatedPayload } from '../shared/formatters';
import { downloadReportExport } from '../shared/exportReport';
import { AgentSummaryReportRow, INITIAL_REPORT_PAGINATION, ReportPaginationState } from '../shared/types';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('reports_issuance_reporting');

const AgentSummaryTab: React.FC = () => {
  const [agentId, setAgentId] = useState('');
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState<'Xlsx' | 'Csv' | null>(null);
  const [rows, setRows] = useState<AgentSummaryReportRow[]>([]);
  const [pagination, setPagination] = useState<ReportPaginationState>(INITIAL_REPORT_PAGINATION);
  const [agents, setAgents] = useState<IAgent[]>([]);
  const agentMap = useMemo(() => buildAgentMap(agents), [agents]);

  useEffect(() => { loadAgentsWithCache().then(setAgents); }, []);

  const buildFilterParams = useCallback(() => ({ AgentId: agentId || undefined }), [agentId]);

  const loadReport = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAgentSummaryReportApi(
        cleanParams({ ...buildFilterParams(), PageIndex: pagination.currentPage, PageSize: pagination.pageSize }),
      );
      const { items, totalItems, totalPages } = getPaginatedPayload(res);
      setRows(items);
      setPagination((prev) => ({ ...prev, totalItems, totalPages }));
    } catch (error: any) {
      toast.error(error?.message || st('khong_the_tai_bao_cao_tong_message'));
    } finally {
      setLoading(false);
    }
  }, [buildFilterParams, pagination.currentPage, pagination.pageSize]);

  useEffect(() => {
    loadReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.currentPage, pagination.pageSize, agentId]);

  const resetPage = () => setPagination((prev) => ({ ...prev, currentPage: 1 }));

  const handleExport = async (format: 'Xlsx' | 'Csv') => {
    setExporting(format);
    try {
      await downloadReportExport(`${ISSUANCE_REPORTS_BASE}/AgentSummary`, buildFilterParams(), format, 'TongHopTheoDaiLy');
    } catch (error: any) {
      toast.error(error?.message || st('khong_the_xuat_bao_cao_message'));
    } finally {
      setExporting(null);
    }
  };

  return (
    <>
      <Row className="g-3 mb-3 align-items-end">
        <Col md={4}>
          <label className="form-label">{st('dai_ly_label')}</label>
          <AgentSelect value={agentId} onChange={(v) => { setAgentId(v); resetPage(); }} />
        </Col>
        <Col md={8} className="d-flex gap-2 justify-content-end">
          <Button color="success" outline size="sm" disabled={!!exporting} onClick={() => handleExport('Xlsx')}>
            {exporting === 'Xlsx' ? <Spinner size="sm" /> : <i className="ri-file-excel-2-line align-bottom me-1"></i>}
            {st('excel_button')}
          </Button>
          <Button color="secondary" outline size="sm" disabled={!!exporting} onClick={() => handleExport('Csv')}>
            {exporting === 'Csv' ? <Spinner size="sm" /> : <i className="ri-file-text-line align-bottom me-1"></i>}
            {st('csv_button')}
          </Button>
        </Col>
      </Row>

      {loading ? (
        <div className="text-center py-5"><Spinner color="primary" /></div>
      ) : (
        <>
          <DataTableShell
            headerRow={
              <tr>
                <th className="text-center" style={{ width: 50 }}>{st('stt_column')}</th>
                <th>{st('dai_ly_label')}</th>
                <th className="text-end">{st('so_ky_tham_gia_column')}</th>
                <th className="text-end">{st('da_giao_column')}</th>
                <th className="text-end">{st('da_ban_column')}</th>
                <th className="text-end">{st('thu_hoi_column')}</th>
                <th className="text-end">{st('ty_le_tieu_thu_column')}</th>
                <th className="text-end">{st('ty_le_thu_hoi_column')}</th>
                <th className="text-end">{st('doanh_thu_ban_column')}</th>
                <th className="text-end">{st('hh_ban_hang_column')}</th>
                <th className="text-end">{st('hh_tra_thuong_column')}</th>
                <th className="text-end">{st('so_ky_da_doi_soat_column')}</th>
              </tr>
            }
          >
            {rows.length === 0 ? (
              <tr><td colSpan={12} className="text-center text-muted py-5">{st('khong_co_du_lieu_text')}</td></tr>
            ) : (
              rows.map((row, index) => (
                <tr key={row.agentId}>
                  <td className="text-center">{getSttNumber(pagination.currentPage, pagination.pageSize, index)}</td>
                  <td>
                    <div className="fw-medium">{getAgentName(agentMap, row.agentId)}</div>
                    <small className="text-muted">{getAgentCode(agentMap, row.agentId)}</small>
                  </td>
                  <td className="text-end">{row.periodCount}</td>
                  <td className="text-end">{formatQty(row.totalDeliveredQuantity)}</td>
                  <td className="text-end text-success">{formatQty(row.totalSoldQuantity)}</td>
                  <td className="text-end">{formatQty(row.totalRecalledQuantity)}</td>
                  <td className="text-end">{formatPercent(row.consumptionRate)}</td>
                  <td className="text-end">{formatPercent(row.recallRate)}</td>
                  <td className="text-end">{formatCurrency(row.totalSoldValue)}</td>
                  <td className="text-end">
                    {formatCurrency(row.salesCommission)}
                    <small className="text-muted d-block">{formatPercent(row.salesCommissionRate)}</small>
                  </td>
                  <td className="text-end">
                    {formatCurrency(row.prizePayoutCommission)}
                    <small className="text-muted d-block">{formatPercent(row.prizePayoutCommissionRate)}</small>
                  </td>
                  <td className="text-end">{row.reconciledPeriodCount}</td>
                </tr>
              ))
            )}
          </DataTableShell>

          <AgentPagination
            currentPage={pagination.currentPage}
            totalPages={pagination.totalPages}
            totalItems={pagination.totalItems}
            pageSize={pagination.pageSize}
            onPageChange={(page) => setPagination((prev) => ({ ...prev, currentPage: page }))}
          />
        </>
      )}
    </>
  );
};

export default AgentSummaryTab;
